import React, { useCallback, useEffect, useState } from "react";
import api, { getProducts, type Product } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

type TeamProduct = {
  id: string;
  due_day: number | null;
  created_at: string;
  product: {
    id: string;
    name: string;
    price_cents: number;
    currency: string;
    recurrence_type: string;
    is_active: boolean;
  };
};

const RECURRENCE: Record<string, string> = {
  hourly: "pe oră",
  weekly: "săptămânal",
  monthly: "lunar",
};

const money = (cents: number, currency: string) =>
  `${(cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: 2 })} ${currency}`;

/**
 * The fees a team carries.
 *
 * A club sells the same thing to a whole group — "Abonament baschet junior" is
 * a property of the junior team, not of thirty children entered one at a time.
 * Attaching it here gives it to everyone on the roster now and to everyone who
 * joins later.
 *
 * Detaching stops future athletes only. What an athlete already owes is a debt,
 * and the screen says so, because a club that expects "remove" to wipe the
 * debts would be very surprised at the end of the month.
 */
export default function TeamProducts({ teamId }: { teamId: string; teamName?: string }) {
  const { activeClubId } = useAuth();

  const [attached, setAttached] = useState<TeamProduct[]>([]);
  const [catalogue, setCatalogue] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [productId, setProductId] = useState("");
  const [dueDay, setDueDay] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mine, all] = await Promise.all([
        api.get<{ products: TeamProduct[] }>(`/teams/${teamId}/products`),
        activeClubId ? getProducts(activeClubId).catch(() => [] as Product[]) : Promise.resolve([]),
      ]);
      setAttached(mine.data.products || []);
      setCatalogue(all);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut încărca serviciile echipei.");
    }
    setLoading(false);
  }, [teamId, activeClubId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productId) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/products`, {
        product_id: productId,
        due_day: dueDay || undefined,
      });
      toast.success(
        data.assigned_to_athletes > 0
          ? `Adăugat. L-am alocat și celor ${data.assigned_to_athletes} sportivi din echipă.`
          : "Adăugat. Sportivii care intră în echipă îl vor primi automat."
      );
      setProductId("");
      setDueDay("");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut adăuga serviciul.");
    }
    setSaving(false);
  };

  const remove = async (row: TeamProduct) => {
    if (
      !window.confirm(
        `Scoți „${row.product.name}” dintre serviciile echipei?\n\nSportivii care intră de acum înainte nu îl vor mai primi. Sumele deja alocate rămân — sunt bani de încasat, nu o setare.`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/teams/${teamId}/products/${row.product.id}`);
      setAttached((prev) => prev.filter((p) => p.id !== row.id));
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut elimina serviciul.");
    }
  };

  const available = catalogue.filter(
    (p) => p.is_active !== false && !attached.some((a) => a.product.id === p.id)
  );

  return (
    <div>
      <p className="text-sm text-slate-500">
        Serviciile de aici se alocă automat fiecărui sportiv din echipă — și celor care intră mai
        târziu.
      </p>

      {loading ? (
        <p className="mt-4 text-sm text-slate-400">Se încarcă...</p>
      ) : (
        <>
          <div className="mt-4 divide-y divide-slate-100">
            {attached.length === 0 && (
              <p className="py-3 text-sm text-slate-400">
                Echipa nu are încă niciun serviciu alocat.
              </p>
            )}
            {attached.map((row) => (
              <div key={row.id} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate text-slate-800">{row.product.name}</p>
                  <p className="text-xs text-slate-500">
                    {money(row.product.price_cents, row.product.currency)}{" "}
                    {RECURRENCE[row.product.recurrence_type] || row.product.recurrence_type}
                    {row.due_day ? ` · scadent pe ${row.due_day}` : ""}
                    {!row.product.is_active && " · produs inactiv"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => remove(row)}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-rose-600"
                >
                  Elimină
                </button>
              </div>
            ))}
          </div>

          <form onSubmit={add} className="flex flex-wrap items-end gap-3 pt-4 mt-2 border-t border-slate-100">
            <div className="flex-1 min-w-[200px]">
              <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="tp-product">
                Serviciu / produs
              </label>
              <select
                id="tp-product"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
              >
                <option value="">Alege...</option>
                {available.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {money(p.price_cents, p.currency)}{" "}
                    {RECURRENCE[p.recurrence_type] || p.recurrence_type}
                  </option>
                ))}
              </select>
              {available.length === 0 && (
                <p className="mt-1 text-xs text-slate-400">
                  Nu mai ai produse de adăugat. Le creezi din pagina Produse și servicii.
                </p>
              )}
            </div>
            <div className="w-28">
              <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="tp-due">
                Zi scadentă
              </label>
              <input
                id="tp-due"
                type="number"
                min={1}
                max={31}
                value={dueDay}
                onChange={(e) => setDueDay(e.target.value)}
                placeholder="opț."
                className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
              />
            </div>
            <button
              type="submit"
              disabled={saving || !productId}
              className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-lg disabled:opacity-50"
            >
              {saving ? "Se adaugă..." : "Adaugă"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
